import type { PlannedPostRead } from '../../types/api';
import { addDays, monthOf, parseIsoDate } from '../calendar/calendarDates';
import { PanelLink } from './PanelLink';
import { THIS_WEEK_DAYS } from './weekPlans';

interface NextSevenDaysProps {
  /** The same week `ThisWeek` lists, see `plansThisWeek`. */
  plans: readonly PlannedPostRead[];
  today: string;
  isLoading: boolean;
  error: string | null;
}

/** Tallest bar in px; the rest scale to it from a zero baseline. */
const BAR_MAX = 80;

function weekday(iso: string): string {
  return parseIsoDate(iso).toLocaleDateString(undefined, { weekday: 'short' });
}

/**
 * Planned posts per day for today and the six after it, as plain CSS bars.
 * Each day says its count in words for a screen reader; today is named, not
 * just coloured.
 */
export function NextSevenDays({ plans, today, isLoading, error }: NextSevenDaysProps) {
  const days = Array.from({ length: THIS_WEEK_DAYS }, (_, i) => {
    const iso = addDays(today, i);
    return { iso, count: plans.filter((plan) => plan.scheduled_on === iso).length };
  });
  const max = Math.max(1, ...days.map((day) => day.count));

  return (
    <section className="home-panel week-chart" aria-labelledby="next-days-title">
      <div className="home-panel__head">
        <h2 className="home-panel__title" id="next-days-title">
          Next 7 days
        </h2>
        <PanelLink to={`/calendar?month=${monthOf(today)}`}>Open calendar</PanelLink>
      </div>

      {isLoading ? (
        <p className="kb-section__status" role="status">
          Reading the calendar
        </p>
      ) : error ? (
        <p className="home-panel__error">
          <span role="alert">{error}</span>
        </p>
      ) : (
        <>
          <ol className="week-chart__days">
            {days.map(({ iso, count }) => {
              const isToday = iso === today;
              const name = isToday ? 'Today' : weekday(iso);
              return (
                <li
                  className={`week-chart__day${isToday ? ' week-chart__day--today' : ''}`}
                  key={iso}
                >
                  <span className="week-chart__count" aria-hidden="true">
                    {count > 0 ? count : ''}
                  </span>
                  <span
                    className={`week-chart__bar${count === 0 ? ' week-chart__bar--empty' : ''}`}
                    aria-hidden="true"
                    style={count > 0 ? { height: (count / max) * BAR_MAX } : undefined}
                  />
                  <span className="week-chart__label">
                    {name}
                    <span className="visually-hidden">
                      : {count} planned {count === 1 ? 'post' : 'posts'}
                    </span>
                  </span>
                </li>
              );
            })}
          </ol>
          <p className="week-chart__legend" aria-hidden="true">
            <span className="week-chart__key week-chart__key--today">Today</span>
            <span className="week-chart__key">Planned posts</span>
          </p>
        </>
      )}
    </section>
  );
}
